import { useState } from "react";
import { toast } from "sonner";
import { HeartHandshake } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function ParentalConsentModal() {
  const { setUser } = useAuth();
  const [form, setForm] = useState({ guardian_name: "", guardian_email: "", guardian_relation: "mae" });
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/parental-consent", { ...form, accepted });
      setUser(data);
      toast.success("Termo aceito! Você já pode navegar livremente.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open>
      <DialogContent className="max-w-md [&>button]:hidden" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} data-testid="parental-consent-modal">
        <DialogHeader>
          <div className="mb-2 grid h-11 w-11 place-items-center rounded-2xl bg-[hsl(var(--gold))]/15 text-[hsl(var(--gold))]">
            <HeartHandshake size={22} />
          </div>
          <DialogTitle className="text-xl">Termo Parental — só uma vez</DialogTitle>
          <DialogDescription>
            Como você tem menos de 18 anos, pedimos que um responsável aceite o termo abaixo. Depois disso, você navega e entra nas salas sem barreiras diárias.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Nome do responsável</Label>
            <Input required value={form.guardian_name} onChange={set("guardian_name")} data-testid="consent-guardian-name" />
          </div>
          <div className="space-y-1.5">
            <Label>E-mail do responsável</Label>
            <Input required type="email" value={form.guardian_email} onChange={set("guardian_email")} data-testid="consent-guardian-email" />
          </div>
          <div className="space-y-1.5">
            <Label>Parentesco</Label>
            <Select value={form.guardian_relation} onValueChange={(v) => setForm({ ...form, guardian_relation: v })}>
              <SelectTrigger data-testid="consent-relation-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="mae">Mãe</SelectItem>
                <SelectItem value="pai">Pai</SelectItem>
                <SelectItem value="tutor">Tutor(a) legal</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 p-3 text-xs leading-relaxed text-muted-foreground">
            <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} className="mt-0.5" data-testid="consent-accept-checkbox" />
            Autorizo o uso do Koiné Live, ciente de que todas as salas são geridas por instituições verificadas, monitoradas pelo Guarda-Costas IA e por moderadores humanos.
          </label>
          <Button type="submit" disabled={!accepted || loading} className="w-full rounded-full" data-testid="consent-submit-button">
            {loading ? "Enviando…" : "Aceitar e continuar"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
